import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionMethodColumns.module.css';

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '40px' },
  { css: css.twoColumns, responsiveImageSizes: '40px' },
  { css: css.threeColumns, responsiveImageSizes: '40px' },
  { css: css.fourColumns, responsiveImageSizes: '40px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock'} blockType
 * @property {Object?} title
 * @property {Object?} text
 * @property {Object?} callToAction
 * @property {Object?} media image field used as the column icon
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Single method column: an icon (from the block's media field), a title, a description and a CTA.
 *
 * @component
 * @param {Object} props
 * @param {BlockConfig} props.block
 * @param {string} props.responsiveImageSizes
 * @param {Object} props.options field options passed down to the Field renderer
 * @returns {JSX.Element|null}
 */
const MethodColumn = props => {
  const { block, responsiveImageSizes, options } = props;
  const { blockId, title, text, callToAction, media } = block || {};

  if (!block) {
    return null;
  }

  const hasIcon = hasDataInFields([media], options);
  const hasCta = hasDataInFields([callToAction], options);
  const hasContent = hasDataInFields([title, text, callToAction], options);

  if (!hasIcon && !hasContent) {
    return null;
  }

  // The icon should never be a link. If the media field carries a link, FieldImage
  // would wrap it in an <a> (making it clickable and picking up the CTA arrow style),
  // so we strip the link before rendering the icon.
  const iconMedia = media ? { ...media, link: undefined } : media;

  return (
    <li className={css.column} id={blockId}>
      {hasIcon ? (
        <div className={css.iconWrapper}>
          <Field data={iconMedia} sizes={responsiveImageSizes} options={options} />
        </div>
      ) : null}
      <Field data={title} className={css.blockTitle} options={options} />
      <Field data={text} className={css.blockText} options={options} />
      {hasCta ? (
        <div className={css.ctaWrapper}>
          <Field data={callToAction} options={options} />
        </div>
      ) : null}
    </li>
  );
};

/**
 * Section that renders a set of method columns (icon + title + description + CTA) as bordered cards.
 * All content (including the icons) comes from the page asset data (blocks).
 * Each block's "media" field is used as the column icon.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses shared classes coming from SectionBuilder
 * @param {string} props.sectionId id of the section (matches the anchor id "method-columns")
 * @param {number?} props.numColumns
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element}
 */
const SectionMethodColumns = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  // Fall back to the number of blocks (capped at 4) when numColumns is not provided
  const columns = numColumns || Math.min(blocks.length || 1, 4);
  const responsiveImageSizes = getResponsiveImageSizes(columns);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={classNames(defaultClasses.sectionDetails, css.header)}>
          <Field
            data={title}
            className={classNames(defaultClasses.title, css.title)}
            options={fieldOptions}
          />
          <Field
            data={description}
            className={classNames(defaultClasses.description, css.description)}
            options={fieldOptions}
          />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlocks ? (
        <ul
          className={classNames(css.columns, getColumnCSS(columns), {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          {blocks.map((block, index) => (
            <MethodColumn
              key={block?.blockId || `method-column-${index}`}
              block={block}
              responsiveImageSizes={responsiveImageSizes}
              options={fieldOptions}
            />
          ))}
        </ul>
      ) : null}
    </SectionContainer>
  );
};

export default SectionMethodColumns;
